// Single source of truth for the "Moments" side-branch (Locket-style capture + routine/habit
// tracking). Pure data + helpers only (no React / no lucide imports) so this file can be
// imported by BOTH the Worker (src/worker.ts, for seeding defaults) and the client.
import type { MomentCategory, MomentCategoryKind, MomentCategoryNode } from "../types";

export type DefaultMomentCategory = {
  slug: string;
  name: string;
  icon: string; // Lucide icon name; the client maps this to a component (see momentIcon.ts)
  kind: MomentCategoryKind;
  children?: DefaultMomentCategory[];
};

// Seeded once per workspace on first access (ensureMomentDefaults in the Worker). Two fixed
// roots (Nếp sinh hoạt / Thói quen) with sensible children; the user adds/edits/nests more.
export const DEFAULT_MOMENT_CATEGORIES: DefaultMomentCategory[] = [
  {
    slug: "routine",
    name: "Nếp sinh hoạt",
    icon: "Sun",
    kind: "routine",
    children: [
      { slug: "wake", name: "Thức dậy", icon: "Sunrise", kind: "routine" },
      { slug: "breakfast", name: "Ăn sáng", icon: "Coffee", kind: "routine" },
      { slug: "lunch", name: "Ăn trưa", icon: "Utensils", kind: "routine" },
      { slug: "dinner", name: "Ăn tối", icon: "UtensilsCrossed", kind: "routine" },
      { slug: "sleep", name: "Đi ngủ", icon: "Moon", kind: "routine" }
    ]
  },
  {
    slug: "habit",
    name: "Thói quen",
    icon: "Target",
    kind: "habit",
    children: [
      { slug: "reading", name: "Đọc sách", icon: "BookOpen", kind: "habit" },
      { slug: "exercise", name: "Tập thể dục", icon: "Dumbbell", kind: "habit" }
    ]
  }
];

// Device-local 'YYYY-MM-DD'. Called on the phone at capture so day-bucketing / streaks are
// timezone-correct (a 11pm capture belongs to today, not tomorrow's UTC date).
export function todayLocalDay(date: Date = new Date()): string {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
}

// Flat rows -> nested tree, sorted by sort_order then name at every level.
export function buildCategoryTree(rows: MomentCategory[]): MomentCategoryNode[] {
  const byId = new Map<string, MomentCategoryNode>();
  rows.forEach((row) => byId.set(row.id, { ...row, children: [] }));
  const roots: MomentCategoryNode[] = [];
  byId.forEach((node) => {
    const parent = node.parentId ? byId.get(node.parentId) : undefined;
    if (parent) {
      parent.children.push(node);
    } else {
      roots.push(node);
    }
  });
  const sortRec = (nodes: MomentCategoryNode[]) => {
    nodes.sort((a, b) => a.sortOrder - b.sortOrder || a.name.localeCompare(b.name));
    nodes.forEach((n) => sortRec(n.children));
  };
  sortRec(roots);
  return roots;
}

// Depth-first flatten (roots first), handy for filter bars and "move to parent" pickers.
export function flattenCategoryTree(
  nodes: MomentCategoryNode[],
  depth = 0
): Array<MomentCategory & { depth: number }> {
  const out: Array<MomentCategory & { depth: number }> = [];
  nodes.forEach((node) => {
    const { children, ...rest } = node;
    out.push({ ...rest, depth });
    out.push(...flattenCategoryTree(children, depth + 1));
  });
  return out;
}

export function isRoutine(kind: MomentCategoryKind): boolean {
  return kind === "routine";
}
